/**
 * Domain Configuration for Educational Resources
 * Following best practices for online exam environments
 */

export type DomainCategory =
  | "encyclopedia"
  | "academic"
  | "mathematics"
  | "science"
  | "programming"
  | "reference"
  | "educational"
  | "government";

export interface DomainConfig {
  domain: string;
  category: DomainCategory;
  canEmbed: boolean;
  searchWeight: number; // 0-1, higher = more relevant for academic use
  description: string;
  searchPriority: number; // 1-10, higher = shown first in results
}

/**
 * Comprehensive list of educational domains suitable for exams
 * All domains have been tested for embedding and reader mode compatibility
 */
export const EDUCATIONAL_DOMAINS: DomainConfig[] = [
  // Encyclopedias & General Reference
  {
    domain: "wikipedia.org",
    category: "encyclopedia",
    canEmbed: true,
    searchWeight: 0.95,
    description: "Free encyclopedia with academic content",
    searchPriority: 10,
  },
  {
    domain: "britannica.com",
    category: "encyclopedia",
    canEmbed: true,
    searchWeight: 0.95,
    description: "Professional encyclopedia with verified content",
    searchPriority: 9,
  },
  {
    domain: "wikimedia.org",
    category: "encyclopedia",
    canEmbed: true,
    searchWeight: 0.85,
    description: "Wikimedia foundation resources",
    searchPriority: 8,
  },

  // Mathematics
  {
    domain: "khanacademy.org",
    category: "mathematics",
    canEmbed: false,
    searchWeight: 1.0,
    description: "Free educational platform with math courses",
    searchPriority: 10,
  },
  {
    domain: "wolframalpha.com",
    category: "mathematics",
    canEmbed: false,
    searchWeight: 1.0,
    description: "Computational knowledge engine",
    searchPriority: 10,
  },
  {
    domain: "mathworld.wolfram.com",
    category: "mathematics",
    canEmbed: true,
    searchWeight: 0.95,
    description: "Comprehensive mathematics resource",
    searchPriority: 9,
  },
  {
    domain: "desmos.com",
    category: "mathematics",
    canEmbed: true,
    searchWeight: 0.9,
    description: "Graphing calculator and math tools",
    searchPriority: 9,
  },
  {
    domain: "geogebra.org",
    category: "mathematics",
    canEmbed: true,
    searchWeight: 0.9,
    description: "Interactive geometry and algebra",
    searchPriority: 8,
  },
  {
    domain: "mathsisfun.com",
    category: "mathematics",
    canEmbed: true,
    searchWeight: 0.85,
    description: "Math explained in simple language",
    searchPriority: 7,
  },

  // Science
  {
    domain: "nature.com",
    category: "science",
    canEmbed: false,
    searchWeight: 0.95,
    description: "Scientific research and journals",
    searchPriority: 9,
  },
  {
    domain: "sciencedirect.com",
    category: "science",
    canEmbed: true,
    searchWeight: 0.95,
    description: "Scientific research database",
    searchPriority: 9,
  },
  {
    domain: "ncbi.nlm.nih.gov",
    category: "science",
    canEmbed: false,
    searchWeight: 0.95,
    description: "National Center for Biotechnology Information",
    searchPriority: 9,
  },
  {
    domain: "pubmed.ncbi.nlm.nih.gov",
    category: "science",
    canEmbed: false,
    searchWeight: 0.95,
    description: "Biomedical literature database",
    searchPriority: 9,
  },
  {
    domain: "chemguide.co.uk",
    category: "science",
    canEmbed: true,
    searchWeight: 0.85,
    description: "Chemistry study guide",
    searchPriority: 7,
  },
  {
    domain: "physics.org",
    category: "science",
    canEmbed: true,
    searchWeight: 0.85,
    description: "Physics resources and education",
    searchPriority: 7,
  },

  // Programming & Technology
  {
    domain: "developer.mozilla.org",
    category: "programming",
    canEmbed: false,
    searchWeight: 1.0,
    description: "MDN Web Docs for web technologies",
    searchPriority: 10,
  },
  {
    domain: "stackoverflow.com",
    category: "programming",
    canEmbed: false,
    searchWeight: 0.95,
    description: "Programming Q&A community",
    searchPriority: 9,
  },
  {
    domain: "github.com",
    category: "programming",
    canEmbed: false,
    searchWeight: 0.9,
    description: "Code repository and documentation",
    searchPriority: 8,
  },
  {
    domain: "w3schools.com",
    category: "programming",
    canEmbed: false,
    searchWeight: 0.85,
    description: "Web development tutorials",
    searchPriority: 7,
  },
  {
    domain: "docs.python.org",
    category: "programming",
    canEmbed: true,
    searchWeight: 0.95,
    description: "Official Python documentation",
    searchPriority: 9,
  },
  {
    domain: "nodejs.org",
    category: "programming",
    canEmbed: true,
    searchWeight: 0.9,
    description: "Node.js documentation",
    searchPriority: 8,
  },
  {
    domain: "reactjs.org",
    category: "programming",
    canEmbed: true,
    searchWeight: 0.9,
    description: "React documentation",
    searchPriority: 8,
  },
  {
    domain: "react.dev",
    category: "programming",
    canEmbed: true,
    searchWeight: 0.9,
    description: "New React documentation",
    searchPriority: 8,
  },

  // Academic & Educational Platforms
  {
    domain: "coursera.org",
    category: "educational",
    canEmbed: false,
    searchWeight: 0.9,
    description: "Online courses from universities",
    searchPriority: 8,
  },
  {
    domain: "edx.org",
    category: "educational",
    canEmbed: false,
    searchWeight: 0.9,
    description: "University-level online courses",
    searchPriority: 8,
  },
  {
    domain: "mit.edu",
    category: "academic",
    canEmbed: false,
    searchWeight: 0.95,
    description: "MIT resources and OpenCourseWare",
    searchPriority: 9,
  },
  {
    domain: "stanford.edu",
    category: "academic",
    canEmbed: false,
    searchWeight: 0.95,
    description: "Stanford University resources",
    searchPriority: 9,
  },
  {
    domain: "harvard.edu",
    category: "academic",
    canEmbed: true,
    searchWeight: 0.95,
    description: "Harvard University resources",
    searchPriority: 9,
  },
  {
    domain: "cambridge.org",
    category: "academic",
    canEmbed: false,
    searchWeight: 0.95,
    description: "Cambridge University Press",
    searchPriority: 9,
  },
  {
    domain: "oxford.edu",
    category: "academic",
    canEmbed: true,
    searchWeight: 0.95,
    description: "Oxford University resources",
    searchPriority: 9,
  },

  // Reference & Dictionaries
  {
    domain: "merriam-webster.com",
    category: "reference",
    canEmbed: true,
    searchWeight: 0.9,
    description: "Dictionary and thesaurus",
    searchPriority: 8,
  },
  {
    domain: "dictionary.com",
    category: "reference",
    canEmbed: false,
    searchWeight: 0.85,
    description: "Online dictionary",
    searchPriority: 7,
  },
  {
    domain: "thesaurus.com",
    category: "reference",
    canEmbed: false,
    searchWeight: 0.85,
    description: "Online thesaurus",
    searchPriority: 7,
  },
  {
    domain: "grammarly.com",
    category: "reference",
    canEmbed: false,
    searchWeight: 0.8,
    description: "Grammar and writing guide",
    searchPriority: 6,
  },

  // Government & Official Sources
  {
    domain: "nasa.gov",
    category: "government",
    canEmbed: false,
    searchWeight: 0.95,
    description: "NASA space and science resources",
    searchPriority: 9,
  },
  {
    domain: "noaa.gov",
    category: "government",
    canEmbed: true,
    searchWeight: 0.9,
    description: "Weather and climate data",
    searchPriority: 8,
  },
  {
    domain: "usgs.gov",
    category: "government",
    canEmbed: false,
    searchWeight: 0.9,
    description: "Geological survey data",
    searchPriority: 8,
  },
  {
    domain: "nih.gov",
    category: "government",
    canEmbed: false,
    searchWeight: 0.95,
    description: "National Institutes of Health",
    searchPriority: 9,
  },
  {
    domain: "cdc.gov",
    category: "government",
    canEmbed: true,
    searchWeight: 0.95,
    description: "Centers for Disease Control",
    searchPriority: 9,
  },
  {
    domain: "archives.gov",
    category: "government",
    canEmbed: false,
    searchWeight: 0.85,
    description: "National Archives",
    searchPriority: 7,
  },

  // Additional Educational Resources
  {
    domain: "quizlet.com",
    category: "educational",
    canEmbed: true,
    searchWeight: 0.8,
    description: "Study sets and flashcards",
    searchPriority: 7,
  },
  {
    domain: "sparknotes.com",
    category: "educational",
    canEmbed: false,
    searchWeight: 0.75,
    description: "Study guides and summaries",
    searchPriority: 6,
  },
  {
    domain: "cliffsnotes.com",
    category: "educational",
    canEmbed: false,
    searchWeight: 0.75,
    description: "Literature study guides",
    searchPriority: 6,
  },
  {
    domain: "purplemath.com",
    category: "mathematics",
    canEmbed: false,
    searchWeight: 0.85,
    description: "Algebra lessons and help",
    searchPriority: 7,
  },
  {
    domain: "mathplanet.com",
    category: "mathematics",
    canEmbed: false,
    searchWeight: 0.8,
    description: "Math courses for high school",
    searchPriority: 6,
  },

  // Stack Exchange Network
  {
    domain: "math.stackexchange.com",
    category: "mathematics",
    canEmbed: false,
    searchWeight: 0.95,
    description: "Mathematics Q&A",
    searchPriority: 9,
  },
  {
    domain: "physics.stackexchange.com",
    category: "science",
    canEmbed: false,
    searchWeight: 0.95,
    description: "Physics Q&A",
    searchPriority: 9,
  },
  {
    domain: "chemistry.stackexchange.com",
    category: "science",
    canEmbed: false,
    searchWeight: 0.95,
    description: "Chemistry Q&A",
    searchPriority: 9,
  },
  {
    domain: "biology.stackexchange.com",
    category: "science",
    canEmbed: false,
    searchWeight: 0.95,
    description: "Biology Q&A",
    searchPriority: 9,
  },
  {
    domain: "english.stackexchange.com",
    category: "reference",
    canEmbed: false,
    searchWeight: 0.9,
    description: "English language Q&A",
    searchPriority: 8,
  },
];

/**
 * Get all allowed domains for quick lookup
 */
export function getAllowedDomains(): string[] {
  return EDUCATIONAL_DOMAINS.map((d) => d.domain);
}

/**
 * Check if a URL is from an allowed educational domain
 */
export function isAllowedDomain(url: string): boolean {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    return EDUCATIONAL_DOMAINS.some((config) => {
      const domain = config.domain.toLowerCase();
      // Check for exact match or subdomain match
      return (
        hostname === domain ||
        hostname.endsWith("." + domain) ||
        hostname === "www." + domain
      );
    });
  } catch {
    return false;
  }
}

/**
 * Get domain configuration by URL
 */
export function getDomainConfig(url: string): DomainConfig | null {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase();

    return (
      EDUCATIONAL_DOMAINS.find((config) => {
        const domain = config.domain.toLowerCase();
        return (
          hostname === domain ||
          hostname.endsWith("." + domain) ||
          hostname === "www." + domain
        );
      }) || null
    );
  } catch {
    return null;
  }
}

/**
 * Get domains by category
 */
export function getDomainsByCategory(category: DomainCategory): DomainConfig[] {
  return EDUCATIONAL_DOMAINS.filter((d) => d.category === category);
}

/**
 * Get search-optimized domain list
 */
export function getSearchDomains(): string[] {
  return EDUCATIONAL_DOMAINS.filter((d) => d.searchWeight >= 0.8)
    .sort((a, b) => b.searchPriority - a.searchPriority)
    .map((d) => d.domain);
}
